import { useCallback, useEffect, useRef, useState } from "react";
import type {
  FoaPart,
  SourcingEventDto,
  SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { isAnnouncedPartI, isPartIHealthSocialEducationService, partRunsInSimplifiedFlow } from "../utils/foaPartDisplay";

export type ReclassificationCause = "regulation" | "serviceCategory" | "contractNature" | "value";

export interface Reclassification {
  from: FoaPart;
  to: FoaPart;
  cause: ReclassificationCause;
}

export interface ReclassificationTracker {
  reclassification: Reclassification | null;
  dismiss: () => void;
  expect: (cause: ReclassificationCause, from: FoaPart | null) => void;
}

export const useReclassification = ({
  foaPart,
  estimatedValueNok,
  isSavingClassificationInput,
  staysInPublicFlow = false,
}: {
  foaPart: FoaPart | null;
  estimatedValueNok: number | null;
  isSavingClassificationInput: boolean;
  staysInPublicFlow?: boolean;
}): ReclassificationTracker => {
  const [reclassification, setReclassification] =
    useState<Reclassification | null>(null);
  const partBeforeChange = useRef<{
    part: FoaPart;
    cause: ReclassificationCause;
  } | null>(null);
  const renderedPart = useRef(foaPart);
  const renderedValue = useRef(estimatedValueNok);
  const partBeforeRegimeWarning = useRef<FoaPart | null>(null);

  useEffect(() => {
    if (estimatedValueNok === renderedValue.current) return;
    renderedValue.current = estimatedValueNok;
    if (partBeforeChange.current != null || renderedPart.current == null)
      return;
    setReclassification(null);
    partBeforeChange.current = {
      part: partBeforeRegimeWarning.current ?? renderedPart.current,
      cause: "value",
    };
  }, [estimatedValueNok]);

  useEffect(() => {
    const before = partBeforeChange.current;
    if (before == null || isSavingClassificationInput) return;
    partBeforeChange.current = null;
    if (foaPart == null) {
      partBeforeRegimeWarning.current = null;
      return;
    }
    const regimeWarningCoversIt =
      before.cause === "value" && partRunsInSimplifiedFlow(foaPart) && !staysInPublicFlow;
    if (regimeWarningCoversIt) {
      partBeforeRegimeWarning.current ??= before.part;
      return;
    }
    partBeforeRegimeWarning.current = null;
    if (foaPart === before.part) return;
    setReclassification({
      from: before.part,
      to: foaPart,
      cause: before.cause,
    });
  }, [isSavingClassificationInput, foaPart, staysInPublicFlow]);

  useEffect(() => {
    renderedPart.current = foaPart;
  }, [foaPart]);

  const dismiss = useCallback(() => setReclassification(null), []);
  const expect = useCallback(
    (cause: ReclassificationCause, from: FoaPart | null) => {
      setReclassification(null);
      partBeforeChange.current = from == null ? null : { part: from, cause };
    },
    [],
  );

  return { reclassification, dismiss, expect };
};

export const useEventReclassification = (
  eventId: string,
): ReclassificationTracker => {
  const { state, fieldStatus } = useEditor<
    SourcingEventDto,
    SourcingEventPatchDto
  >(eventId);
  const isSaving = (field: string) =>
    ["saving", "pending"].includes(fieldStatus(field));
  const estimatedValue = state.eventContext.estimatedValue?.value;
  const publicContext = state.eventContext.type === "Public" ? state.eventContext : null;
  const foaPart = publicContext?.foaPart ?? null;
  return useReclassification({
    foaPart,
    staysInPublicFlow:
      isAnnouncedPartI(publicContext) ||
      isPartIHealthSocialEducationService(
        publicContext?.regulation,
        foaPart,
        publicContext?.contractNature,
        publicContext?.serviceCategory,
      ),
    estimatedValueNok: estimatedValue != null ? Number(estimatedValue) : null,
    isSavingClassificationInput:
      isSaving("publicContext.regulation") ||
      isSaving("publicContext.serviceCategory") ||
      isSaving("eventContext.contractNature") ||
      isSaving("eventContext.estimatedValue"),
  });
};
